import { SpeciesInfo } from '../../../shared/kernel/types';

export const INITIAL_SPECIES_DATABASE: SpeciesInfo[] = [
  // ================= TIER 1: PRODUCERS (생산자 / 식물 & 조류) =================
  {
    id: 'lumi_flora',
    name: '루미 플로라',
    scientificName: 'Phytolumina vulgaris',
    trophicLevel: 'producer',
    tier: 1,
    description: '작은 빛을 머금고 천천히 부유하는 기본 식물성 플랑크톤. 생태계의 기초 영양을 담당합니다.',
    lore: '유리병의 바닥과 수중에서 부드러운 연둣빛 광채를 뿜어내며, 빛과 수분을 통해 산소를 생산합니다.',
    evolutionHint: '기본 생물 (시작부터 해금)',
    unlocked: true,
    baseGenome: {
      size: 1.0,
      speed: 0.6,
      metabolism: 0.8,
      tempOpt: 22,
      tempTol: 10,
      moistOpt: 60,
      hue: 135, // Neon Green
      mutationRate: 0.15,
      defense: 0.1,
      bioluminescence: 0.7,
    },
    spawnConditions: {},
    iconEmoji: '🌿',
    glowColor: '#4ade80',
  },
  {
    id: 'solar_bloom',
    name: '솔라 블룸',
    scientificName: 'Heliosphaera magnifica',
    trophicLevel: 'producer',
    tier: 1,
    description: '강렬한 햇빛을 흡수하여 황금빛 꽃잎을 펼치는 거대 광합성 식물.',
    lore: '일조량이 80% 이상인 따뜻한 환경에서 번성하며, 주변 미생물들에게 풍부한 활력 에너지를 방출합니다.',
    evolutionHint: '일조량 80% 이상 & 온도 25°C 이상 유지 시 루미 플로라에서 진화',
    unlocked: false,
    baseGenome: {
      size: 1.6,
      speed: 0.4,
      metabolism: 0.6,
      tempOpt: 32,
      tempTol: 8,
      moistOpt: 50,
      hue: 45, // Golden Sun
      mutationRate: 0.2,
      defense: 0.3,
      bioluminescence: 1.0,
    },
    spawnConditions: {
      minSun: 80,
      minTemp: 25,
      parentSpeciesId: 'lumi_flora',
    },
    iconEmoji: '☀️',
    glowColor: '#fbbf24',
  },
  {
    id: 'aqua_kelp',
    name: '아쿠아 켈프',
    scientificName: 'Hydrospora fluida',
    trophicLevel: 'producer',
    tier: 1,
    description: '습도가 높은 환경에서 리듬감 있게 춤추는 수생 해초형 미생물 집합체.',
    lore: '유리병 내부에 수분이 가득 찰 때 포자를 급격히 흩뿌리며 물 속의 유해 물질을 흡수합니다.',
    evolutionHint: '수분 80% 이상 유지 시 루미 플로라에서 진화',
    unlocked: false,
    baseGenome: {
      size: 1.3,
      speed: 0.8,
      metabolism: 0.7,
      tempOpt: 20,
      tempTol: 12,
      moistOpt: 85,
      hue: 185, // Cyan Ocean
      mutationRate: 0.18,
      defense: 0.2,
      bioluminescence: 0.8,
    },
    spawnConditions: {
      minMoist: 80,
      parentSpeciesId: 'lumi_flora',
    },
    iconEmoji: '🌊',
    glowColor: '#38bdf8',
  },
  {
    id: 'crystal_leaf',
    name: '크리스탈 리프',
    scientificName: 'Cryofrondis crystallus',
    trophicLevel: 'producer',
    tier: 1,
    description: '영하의 극한 환경에서만 결정화되어 자라나는 푸른 얼음 수정 식물.',
    lore: '차가운 유리벽 표면에 기하학적인 눈꽃 결정을 만들며, 저온에서도 얼지 않는 부동액 수액을 분비합니다.',
    evolutionHint: '온도 5°C 이하 (빙하기) 유지 시 돌연변이 탄생',
    unlocked: false,
    baseGenome: {
      size: 1.2,
      speed: 0.3,
      metabolism: 0.4,
      tempOpt: -2,
      tempTol: 8,
      moistOpt: 40,
      hue: 205, // Ice Blue
      mutationRate: 0.25,
      defense: 0.6,
      bioluminescence: 0.9,
    },
    spawnConditions: {
      maxTemp: 5,
      parentSpeciesId: 'lumi_flora',
    },
    iconEmoji: '❄️',
    glowColor: '#93c5fd',
  },

  // ================= TIER 2: PRIMARY CONSUMERS (1차 소비자 / 초식 미생물) =================
  {
    id: 'jelly_wiggle',
    name: '젤리 위글',
    scientificName: 'Mollusca gelatinosa',
    trophicLevel: 'herbivore',
    tier: 2,
    description: '반투명한 분홍빛 몸체로 통통 튀며 식물성 플랑크톤을 섭취하는 귀여운 초식 생물.',
    lore: '고양이 젤리처럼 부드럽고 말랑거리며, 배가 부르면 작은 기쁨의 방울을 내뿜습니다.',
    evolutionHint: '기본 1차 소비자 (시작부터 해금)',
    unlocked: true,
    baseGenome: {
      size: 1.1,
      speed: 1.2,
      metabolism: 1.0,
      tempOpt: 22,
      tempTol: 10,
      moistOpt: 60,
      hue: 330, // Pastel Pink
      mutationRate: 0.15,
      defense: 0.2,
      bioluminescence: 0.6,
    },
    spawnConditions: {},
    iconEmoji: '🫧',
    glowColor: '#f472b6',
  },
  {
    id: 'glow_tail',
    name: '글로우 테일',
    scientificName: 'Caudalux agilis',
    trophicLevel: 'herbivore',
    tier: 2,
    description: '꼬리 끝에서 별가루 입자를 반짝이며 빠르게 유영하는 날렵한 초식 생물.',
    lore: '포식자의 기운을 감지하면 순간적으로 꼬리 불빛을 점멸시켜 혼란을 주고 도망칩니다.',
    evolutionHint: '속도 유전 수치 1.6 이상 & 수분 70% 이상에서 젤리 위글에서 분화',
    unlocked: false,
    baseGenome: {
      size: 0.9,
      speed: 2.2,
      metabolism: 1.3,
      tempOpt: 24,
      tempTol: 9,
      moistOpt: 70,
      hue: 55, // Electric Yellow
      mutationRate: 0.2,
      defense: 0.1,
      bioluminescence: 0.95,
    },
    spawnConditions: {
      minMoist: 70,
      parentSpeciesId: 'jelly_wiggle',
    },
    iconEmoji: '✨',
    glowColor: '#fde047',
  },
  {
    id: 'shell_pod',
    name: '쉘 포드',
    scientificName: 'Testudina lorica',
    trophicLevel: 'herbivore',
    tier: 2,
    description: '단단하고 투명한 껍질을 지닌 완보동물형 생물. 포식자의 공격을 완벽히 방어합니다.',
    lore: '느리지만 끈질긴 생명력을 가졌으며, 유리병 환경이 악화되어도 가장 오래 생존합니다.',
    evolutionHint: '방어력 유전 수치 0.5 이상 & 영양소 60% 이상에서 진화',
    unlocked: false,
    baseGenome: {
      size: 1.4,
      speed: 0.7,
      metabolism: 0.6,
      tempOpt: 20,
      tempTol: 15,
      moistOpt: 50,
      hue: 160, // Emerald Shield
      mutationRate: 0.12,
      defense: 0.9,
      bioluminescence: 0.4,
    },
    spawnConditions: {
      minNutrients: 60,
      parentSpeciesId: 'jelly_wiggle',
    },
    iconEmoji: '🛡️',
    glowColor: '#34d399',
  },
  {
    id: 'aurora_fin',
    name: '오로라 핀',
    scientificName: 'Pinna iridescentis',
    trophicLevel: 'herbivore',
    tier: 2,
    description: '빛의 각도에 따라 무지개색으로 변하는 오로라 지느러미를 펄럭이는 환상종.',
    lore: '오로라 핀이 지나간 자리에는 잔잔한 치유 파동이 남아 주변 생물들의 체력을 회복시킵니다.',
    evolutionHint: '돌연변이 촉매제(Mutagen) 섭취 & 온도 30°C 이상에서 희귀 변이',
    unlocked: false,
    baseGenome: {
      size: 1.5,
      speed: 1.5,
      metabolism: 0.9,
      tempOpt: 28,
      tempTol: 10,
      moistOpt: 75,
      hue: 280, // Aurora Purple
      mutationRate: 0.3,
      defense: 0.4,
      bioluminescence: 1.0,
    },
    spawnConditions: {
      minTemp: 30,
      parentSpeciesId: 'jelly_wiggle',
    },
    iconEmoji: '🌈',
    glowColor: '#c084fc',
  },

  // ================= TIER 3: PREDATORS (2차/최상위 포식자 / 육식 미생물) =================
  {
    id: 'phantom_lip',
    name: '팬텀 립',
    scientificName: 'Vorax umbrosus',
    trophicLevel: 'predator',
    tier: 3,
    description: '보랏빛 반투명 몸체로 초식 생물을 부드럽게 감싸 섭취하는 우아한 포식자.',
    lore: '생태계의 과도한 초식 생물 개체수를 조절하여 식물 멸종을 방지하는 수호자 역할을 합니다.',
    evolutionHint: '기본 포식자 (시작부터 해금)',
    unlocked: true,
    baseGenome: {
      size: 1.5,
      speed: 1.4,
      metabolism: 1.2,
      tempOpt: 22,
      tempTol: 11,
      moistOpt: 60,
      hue: 265, // Royal Violet
      mutationRate: 0.15,
      defense: 0.3,
      bioluminescence: 0.8,
    },
    spawnConditions: {},
    iconEmoji: '👁️',
    glowColor: '#a78bfa',
  },
  {
    id: 'spike_hunter',
    name: '스파이크 헌터',
    scientificName: 'Aculeus velox',
    trophicLevel: 'predator',
    tier: 3,
    description: '황금빛 뿔과 가시로 무장하여 전방으로 번개처럼 돌진하는 맹렬한 사냥꾼.',
    lore: '빠른 먹이를 사냥하기 위해 진화했으며, 사냥에 성공하면 승리의 섬광 펄스를 방출합니다.',
    evolutionHint: '초식 생물 개체수 15마리 이상 번성 시 팬텀 립에서 분화',
    unlocked: false,
    baseGenome: {
      size: 1.3,
      speed: 2.6,
      metabolism: 1.6,
      tempOpt: 26,
      tempTol: 8,
      moistOpt: 55,
      hue: 15, // Crimson Gold
      mutationRate: 0.22,
      defense: 0.7,
      bioluminescence: 0.85,
    },
    spawnConditions: {
      parentSpeciesId: 'phantom_lip',
    },
    iconEmoji: '⚡',
    glowColor: '#f87171',
  },
  {
    id: 'nebula_kraken',
    name: '네뷸라 크라켄',
    scientificName: 'Architeuthis stellaris',
    trophicLevel: 'predator',
    tier: 3,
    description: '우주 성운처럼 신비로운 촉수와 별빛을 뿜어내는 전설의 최상위 미니 크라켄.',
    lore: '모든 생태계 균형 지수가 85% 이상 60초간 유지될 때 차원의 틈에서 탄생합니다.',
    evolutionHint: '생태계 건강도 85% 이상 & 4단계 트로픽 공존 시 전설 탄생',
    unlocked: false,
    baseGenome: {
      size: 2.2,
      speed: 1.1,
      metabolism: 0.7,
      tempOpt: 22,
      tempTol: 15,
      moistOpt: 80,
      hue: 240, // Deep Cosmic Blue
      mutationRate: 0.35,
      defense: 0.8,
      bioluminescence: 1.0,
    },
    spawnConditions: {
      parentSpeciesId: 'phantom_lip',
      specialCondition: 'harmony_high',
    },
    iconEmoji: '🐙',
    glowColor: '#818cf8',
  },

  // ================= TIER 4: DECOMPOSERS & SYMBIOTES (분해자 / 균사 & 정화자) =================
  {
    id: 'mycel_linker',
    name: '미셀 링커',
    scientificName: 'Mycelium nexus',
    trophicLevel: 'decomposer',
    tier: 4,
    description: '바닥 토양에 버섯 모양 균사를 뻗어 사체와 노폐물을 유기 영양염류로 환원하는 분해자.',
    lore: '생명의 끝을 새로운 시작으로 이어주는 생태계 순환의 핵심 고리입니다.',
    evolutionHint: '기본 분해자 (시작부터 해금)',
    unlocked: true,
    baseGenome: {
      size: 1.1,
      speed: 0.5,
      metabolism: 0.5,
      tempOpt: 23,
      tempTol: 12,
      moistOpt: 70,
      hue: 28, // Warm Amber Mushroom
      mutationRate: 0.15,
      defense: 0.4,
      bioluminescence: 0.65,
    },
    spawnConditions: {},
    iconEmoji: '🍄',
    glowColor: '#fb923c',
  },
  {
    id: 'bio_purifier',
    name: '바이오 정제기',
    scientificName: 'Sphaera purgatoria',
    trophicLevel: 'decomposer',
    tier: 4,
    description: '유독 가스와 노폐물을 흡수하여 맑은 산소 정화 방울을 퐁퐁 터뜨리는 힐링 구체.',
    lore: '테라리움 내부 수질을 항상 청정하게 유지시켜 모든 생물의 수명을 연장합니다.',
    evolutionHint: '수분 75% 이상 & 수중 노폐물 정화 시 진화',
    unlocked: false,
    baseGenome: {
      size: 1.4,
      speed: 0.9,
      metabolism: 0.4,
      tempOpt: 21,
      tempTol: 10,
      moistOpt: 80,
      hue: 170, // Pure Mint Aquamarine
      mutationRate: 0.18,
      defense: 0.5,
      bioluminescence: 0.9,
    },
    spawnConditions: {
      minMoist: 75,
      parentSpeciesId: 'mycel_linker',
    },
    iconEmoji: '🔮',
    glowColor: '#2dd4bf',
  },
  {
    id: 'aether_spore',
    name: '에테르 스포어',
    scientificName: 'Sporea aetheria',
    trophicLevel: 'decomposer',
    tier: 4,
    description: '사멸한 생물의 영혼 에너지를 모아 환상적인 청록빛 진화 포자를 흩뿌리는 신비의 균류.',
    lore: '이 포자를 흡수한 미생물은 다음 세대에서 매우 희귀한 돌연변이 형질을 얻게 됩니다.',
    evolutionHint: '누적 사멸 개체수 20마리 이상 분해 시 희귀 각성',
    unlocked: false,
    baseGenome: {
      size: 1.3,
      speed: 0.6,
      metabolism: 0.3,
      tempOpt: 24,
      tempTol: 14,
      moistOpt: 65,
      hue: 300, // Magenta Aether
      mutationRate: 0.4,
      defense: 0.6,
      bioluminescence: 1.0,
    },
    spawnConditions: {
      parentSpeciesId: 'mycel_linker',
    },
    iconEmoji: '🌌',
    glowColor: '#e879f9',
  },

  // ================= SPECIAL HIDDEN EVOLUTIONS =================
  {
    id: 'cosmic_plankton',
    name: '코스믹 플랑크톤',
    scientificName: 'Cosmoplankton astra',
    trophicLevel: 'producer',
    tier: 4,
    description: '밤하늘의 오로라와 은하수 에너지를 모아 별빛을 발산하는 궁극의 발광 생물.',
    lore: '낮과 밤의 순환이 5회 이상 지나고 모든 도감이 60% 이상 해금되었을 때 홀연히 나타납니다.',
    evolutionHint: '야간 시간대(Night) & 높은 생물 다양성 달성 시 신비 출현',
    unlocked: false,
    baseGenome: {
      size: 1.5,
      speed: 1.0,
      metabolism: 0.5,
      tempOpt: 20,
      tempTol: 20,
      moistOpt: 70,
      hue: 220,
      mutationRate: 0.3,
      defense: 0.7,
      bioluminescence: 1.0,
    },
    spawnConditions: {
      specialCondition: 'night_cosmic',
    },
    iconEmoji: '⭐',
    glowColor: '#60a5fa',
  },
  {
    id: 'prism_amoeba',
    name: '프리즘 아메바',
    scientificName: 'Amoeba prismatica',
    trophicLevel: 'herbivore',
    tier: 4,
    description: '주변 환경의 색과 온도에 따라 실시간으로 신체 색상과 굴절률을 변이시키는 카멜레온 아메바.',
    lore: '환경의 극적인 변화에도 유연하게 모습을 바꾸며 어떤 기후에서도 번성할 수 있습니다.',
    evolutionHint: '급격한 온도 변화(-10°C <-> 40°C)를 견뎌낸 개체에서 발현',
    unlocked: false,
    baseGenome: {
      size: 1.6,
      speed: 1.3,
      metabolism: 0.8,
      tempOpt: 25,
      tempTol: 25,
      moistOpt: 60,
      hue: 120,
      mutationRate: 0.5,
      defense: 0.6,
      bioluminescence: 0.95,
    },
    spawnConditions: {
      specialCondition: 'thermal_shock',
    },
    iconEmoji: '💎',
    glowColor: '#a3e635',
  },
];
